import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Building2, Store, Users, ArrowRight, ArrowLeft, Loader2,
  Lock, Eye, EyeOff, Check, Network, LayoutDashboard, MailCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { VsMark } from "@/components/Logo";
import { OtpInput, maskEmail } from "@/components/OtpInput";

/* ------------------------------ Shared pieces ------------------------------ */
const TRUST_POINTS = [
  { icon: ShieldCheck, t: "Secure Business Access" },
  { icon: LayoutDashboard, t: "Role-Based Workspaces" },
  { icon: Network, t: "Connected Business Operations" },
];

function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between bg-vs-deep p-12 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      <div className="absolute -bottom-40 -right-40 h-[480px] w-[480px] rounded-full bg-vs-royal/15 blur-3xl" />
      <Link to="/" className="relative flex items-center gap-3">
        <VsMark tile className="h-10 w-10" />
        <div>
          <div className="font-display font-extrabold text-white text-lg leading-tight">VS PartnerHub</div>
          <div className="text-[10px] text-blue-300">Powered by Vijay Software Solutions</div>
        </div>
      </Link>

      <div className="relative">
        <div className="text-blue-300 text-xs font-semibold tracking-[0.2em] uppercase mb-4">Connect. Source. Collaborate. Grow.</div>
        <h2 className="font-display font-extrabold text-4xl text-white leading-tight text-balance">
          One connected platform for enterprise partnerships.
        </h2>
        <p className="text-slate-300 mt-4 max-w-md leading-relaxed">
          One connected platform for enterprise clients, trusted vendors and staffing partners.
        </p>
        <div className="mt-8 flex items-center gap-3">
          {["Client", "Vendor", "Staffing Partner"].map((r, i) => (
            <React.Fragment key={r}>
              <div className="rounded-lg border border-white/15 bg-white/5 px-3.5 py-2 text-xs font-semibold text-blue-100">{r}</div>
              {i < 2 && <div className="h-px w-5 bg-white/25" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="relative flex items-center gap-6">
        {TRUST_POINTS.map((t) => (
          <div key={t.t} className="flex items-center gap-2">
            <t.icon className="h-4 w-4 text-blue-300" />
            <span className="text-xs text-slate-300">{t.t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MobileBrand() {
  return (
    <div className="lg:hidden flex items-center gap-2.5 mb-6">
      <VsMark className="h-9 w-9" />
      <div className="leading-tight">
        <div className="font-display font-extrabold text-vs-ink">VS PartnerHub</div>
        <div className="text-[10px] text-slate-500">Powered by Vijay Software Solutions</div>
      </div>
    </div>
  );
}

const ROLE_TILES = [
  { id: "client", label: "Client", desc: "Client Workspace", icon: Building2 },
  { id: "vendor", label: "Vendor", desc: "Vendor Workspace", icon: Store },
  { id: "staffing", label: "Staffing Partner", desc: "Staffing Workspace", icon: Users },
];

function RoleTiles({ role, setRole }) {
  return (
    <div className="grid grid-cols-3 gap-2.5" role="radiogroup" aria-label="Workspace type">
      {ROLE_TILES.map((r) => {
        const active = role === r.id;
        return (
          <button key={r.id} type="button" role="radio" aria-checked={active} onClick={() => setRole(r.id)} data-testid={`role-select-${r.id}`}
            className={`relative text-left rounded-lg border p-3.5 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vs-royal ${
              active ? "border-vs-royal bg-vs-light/60 ring-1 ring-vs-royal shadow-sm" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"}`}>
            {active && (
              <span className="absolute top-2 right-2 h-4.5 w-4.5 h-[18px] w-[18px] rounded-full bg-vs-royal flex items-center justify-center">
                <Check className="h-3 w-3 text-white" strokeWidth={3} />
              </span>
            )}
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-2.5 transition-colors ${active ? "bg-vs-royal" : "bg-slate-100"}`}>
              <r.icon className={`h-4.5 w-4.5 h-[18px] w-[18px] transition-colors ${active ? "text-white" : "text-slate-500"}`} />
            </div>
            <div className={`text-[13px] font-bold tracking-wide ${active ? "text-vs-royal" : "text-slate-800"}`}>{r.label}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{r.desc}</div>
          </button>
        );
      })}
    </div>
  );
}

function PasswordInput({ id, value, onChange, testid, placeholder = "••••••••", autoComplete }) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative mt-1.5">
      <Input id={id} type={show ? "text" : "password"} value={value} onChange={onChange} required
        placeholder={placeholder} autoComplete={autoComplete} data-testid={testid} className="pr-10" />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"}
        data-testid={`${testid}-toggle`}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

const PASSWORD_RULES = [
  { id: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "num", label: "One number", test: (p) => /\d/.test(p) },
  { id: "special", label: "One special character", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function PasswordRules({ password }) {
  if (!password) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid="password-rules">
      {PASSWORD_RULES.map((r) => {
        const ok = r.test(password);
        return (
          <div key={r.id} className={`flex items-center gap-1.5 text-[11px] ${ok ? "text-emerald-600" : "text-slate-400"}`}>
            <span className={`h-3.5 w-3.5 rounded-full flex items-center justify-center border ${ok ? "bg-emerald-500 border-emerald-500" : "border-slate-300"}`}>
              {ok && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3.5} />}
            </span>
            {r.label}
          </div>
        );
      })}
    </div>
  );
}

function friendlyAuthError(detail, fallback = "Something went wrong. Please try again.") {
  const msg = apiError(detail) || "";
  if (/invalid email or password/i.test(msg)) return "Incorrect email or password.";
  if (/expired/i.test(msg)) return "Verification code expired. Please request a new code.";
  if (/already exists/i.test(msg)) return "An account with this email already exists. Try signing in instead.";
  return msg || fallback;
}

const ErrorBox = ({ children, testid }) => (
  <div role="alert" className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3.5 py-2.5" data-testid={testid}>{children}</div>
);

const LoadingLabel = ({ loading, text, children }) =>
  loading ? <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{text}</span> : children;

/* ---------------------------------- Login ---------------------------------- */
export function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = React.useState("client");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true); setError("");
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role !== "super_admin" && !u.onboarding_complete ? "/onboarding" : "/app/overview");
    } catch (err) {
      setError(friendlyAuthError(err.response?.data?.detail, "Unable to sign in. Please try again."));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-center justify-center px-5 py-8 sm:p-12 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <MobileBrand />
          <h1 className="font-display font-bold text-2xl sm:text-[28px] text-vs-ink tracking-tight">Welcome to VS PartnerHub</h1>
          <p className="text-sm text-slate-500 mt-1.5">Sign in securely to access your business workspace.</p>

          <div className="mt-6"><RoleTiles role={role} setRole={setRole} /></div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Work Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" required autoComplete="email" data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-xs font-medium text-vs-royal hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password-input" autoComplete="current-password" />
            </div>
            {error && <ErrorBox testid="login-error">{error}</ErrorBox>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-vs-royal hover:bg-vs-blue text-white font-semibold shadow-sm transition-all" data-testid="login-submit-btn">
              <LoadingLabel loading={loading} text="Authenticating…">Sign In<ArrowRight className="ml-1.5 h-4 w-4" /></LoadingLabel>
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            New partner? <Link to="/register" className="text-vs-royal font-semibold hover:underline">Create an account</Link>
          </p>
          <div className="mt-6 pt-4 border-t border-slate-200/70 flex items-center justify-center gap-5 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" />Secure Authentication</span>
            <span className="inline-flex items-center gap-1.5"><MailCheck className="h-3 w-3" />Email Verification</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="h-3 w-3" />Protected Data</span>
          </div>
          <div className="mt-4 text-center">
            <Link to="/secure-admin" className="text-[11px] text-slate-300 hover:text-slate-500 inline-flex items-center gap-1 transition-colors" data-testid="secure-admin-link">
              <Lock className="h-3 w-3" />Secure management access
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

/* -------------------------------- Admin Login -------------------------------- */
export function AdminLogin() {
  const nav = useNavigate();
  const { adminLogin, logout } = useAuth();
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true); setError("");
    try {
      const u = await adminLogin(password);
      if (u.role !== "super_admin") {
        await logout();
        setError("These credentials do not have management access.");
        return;
      }
      toast.success("Welcome to VS Command Center");
      nav("/app/overview");
    } catch (err) {
      setError(friendlyAuthError(err.response?.data?.detail, "Authentication failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-vs-deep relative overflow-hidden p-6">
      <div className="absolute inset-0 grid-pattern opacity-20" />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <VsMark className="h-14 w-14" />
            <div className="font-display font-extrabold text-vs-ink mt-3 text-lg">VS Command Center</div>
            <div className="text-[11px] text-slate-500">Secure management access</div>
          </div>
          <div className="flex items-center gap-2 rounded-lg bg-rose-50 border border-rose-100 px-3 py-2 text-xs text-rose-700 mb-5">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> Restricted area — VS-Solutions platform administrators only.
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="admin-password" className="text-slate-700">Administrator Password</Label>
              <PasswordInput id="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} testid="admin-password-input" placeholder="Enter administrator password" autoComplete="current-password" />
            </div>
            {error && <ErrorBox testid="admin-login-error">{error}</ErrorBox>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-vs-deep hover:bg-vs-navy text-white font-semibold" data-testid="admin-login-submit">
              <LoadingLabel loading={loading} text="Authenticating…">Secure Sign In<ArrowRight className="ml-1.5 h-4 w-4" /></LoadingLabel>
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-6 text-center"><Link to="/login" className="hover:text-slate-600">← Back to partner login</Link></p>
        </div>
      </motion.div>
    </div>
  );
}

/* --------------------------------- Register --------------------------------- */
const REGISTER_ROLES = [
  { id: "client", label: "Client", icon: Building2, desc: "For enterprises managing requirements, vendors, procurement and workforce operations." },
  { id: "vendor", label: "Vendor", icon: Store, desc: "For businesses providing products, services and technology solutions." },
  { id: "staffing", label: "Staffing Partner", icon: Users, desc: "For workforce and staffing companies managing candidate pipelines and deployments." },
];

const REGISTER_STEPS = ["Organization Type", "Account", "Verification"];

function StepIndicator({ step }) {
  return (
    <div className="flex items-center gap-1.5 mb-7" data-testid="register-step-indicator">
      {REGISTER_STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <span className={`h-6 w-6 rounded-full text-[11px] font-bold flex items-center justify-center transition-colors ${
              step > i ? "bg-emerald-500 text-white" : step === i ? "bg-vs-royal text-white" : "bg-slate-100 text-slate-400"}`}>
              {step > i ? <Check className="h-3 w-3" strokeWidth={3.5} /> : String(i + 1).padStart(2, "0")}
            </span>
            <span className={`text-[11px] font-semibold hidden sm:block ${step === i ? "text-vs-ink" : "text-slate-400"}`}>{s}</span>
          </div>
          {i < REGISTER_STEPS.length - 1 && <div className={`h-px flex-1 min-w-[12px] ${step > i ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export function Register() {
  const nav = useNavigate();
  const loc = useLocation();
  const { register, verifyEmail, resendOtp } = useAuth();
  const params = new URLSearchParams(loc.search);
  const [step, setStep] = React.useState(0);
  const [role, setRole] = React.useState(params.get("role") || "");
  const [form, setForm] = React.useState({ name: "", email: "", password: "", organization_name: "" });
  const [otp, setOtp] = React.useState("");
  const [cooldown, setCooldown] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const passwordValid = PASSWORD_RULES.every((r) => r.test(form.password));

  const startRegistration = async (e) => {
    e.preventDefault();
    if (loading || !passwordValid) return;
    setLoading(true); setError("");
    try {
      await register({ ...form, role });
      setStep(2); setCooldown(60);
      toast.success("Verification code sent to your email");
    } catch (err) { setError(friendlyAuthError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doVerify = async () => {
    if (otp.length !== 6 || loading) { if (otp.length !== 6) setError("Enter the 6-digit code"); return; }
    setLoading(true); setError("");
    try {
      await verifyEmail(form.email, otp);
      toast.success("Email verified — let's set up your workspace");
      nav("/onboarding");
    } catch (err) { setError(friendlyAuthError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doResend = async () => {
    setError("");
    try { await resendOtp(form.email); setCooldown(60); toast.success("A new code was sent"); }
    catch (err) { setError(friendlyAuthError(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <BrandPanel />
      <div className="flex items-start lg:items-center justify-center px-5 py-8 sm:p-12 bg-slate-50 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <MobileBrand />
          <StepIndicator step={step} />

          {step === 0 && (
            <>
              <h1 className="font-display font-bold text-2xl sm:text-[28px] text-vs-ink tracking-tight">Create Your VS PartnerHub Account</h1>
              <p className="text-sm text-slate-500 mt-1.5">Join the VS PartnerHub ecosystem and access your dedicated business workspace.</p>
              <p className="text-sm font-semibold text-slate-700 mt-6 mb-3">What type of organization are you?</p>
              <div className="space-y-2.5" role="radiogroup" aria-label="Organization type">
                {REGISTER_ROLES.map((r) => (
                  <button key={r.id} type="button" role="radio" aria-checked={role === r.id}
                    onClick={() => { setRole(r.id); setStep(1); }} data-testid={`register-role-${r.id}`}
                    className="w-full flex items-start gap-3.5 rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-vs-royal hover:shadow-sm transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vs-royal">
                    <div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center shrink-0 group-hover:bg-vs-royal transition-colors">
                      <r.icon className="h-5 w-5 text-vs-royal group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 tracking-wide">{r.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">{r.desc}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-vs-royal mt-1 transition-colors shrink-0" />
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <button onClick={() => setStep(0)} className="text-xs text-slate-400 hover:text-slate-600 mb-4 inline-flex items-center gap-1" data-testid="register-back-role">
                <ArrowLeft className="h-3 w-3" />Change organization type
              </button>
              <h1 className="font-display font-bold text-2xl sm:text-[28px] text-vs-ink tracking-tight">Account details</h1>
              <p className="text-sm text-slate-500 mt-1.5">
                Registering as <span className="font-semibold text-vs-royal">{REGISTER_ROLES.find((r) => r.id === role)?.label}</span>. Your work email is your identity — verified by a one-time code.
              </p>
              <form onSubmit={startRegistration} className="mt-6 space-y-4">
                <div><Label className="text-slate-700">Organization name</Label><Input value={form.organization_name} onChange={set("organization_name")} required data-testid="register-org-input" className="mt-1.5" placeholder="Acme Corp Pvt Ltd" /></div>
                <div><Label className="text-slate-700">Your full name</Label><Input value={form.name} onChange={set("name")} required data-testid="register-name-input" className="mt-1.5" autoComplete="name" /></div>
                <div><Label className="text-slate-700">Work email</Label><Input type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" className="mt-1.5" placeholder="you@company.com" autoComplete="email" /></div>
                <div>
                  <Label className="text-slate-700">Create password</Label>
                  <PasswordInput id="reg-password" value={form.password} onChange={set("password")} testid="register-password-input" placeholder="Create a strong password" autoComplete="new-password" />
                  <PasswordRules password={form.password} />
                </div>
                {error && <ErrorBox testid="register-error">{error}</ErrorBox>}
                <Button type="submit" disabled={loading || !passwordValid} className="w-full h-11 bg-vs-royal hover:bg-vs-blue text-white font-semibold" data-testid="register-submit-btn">
                  <LoadingLabel loading={loading} text="Creating your account…">Continue with Email<ArrowRight className="ml-1.5 h-4 w-4" /></LoadingLabel>
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <div className="text-center">
              <div className="h-12 w-12 rounded-xl bg-vs-light flex items-center justify-center mx-auto mb-4"><MailCheck className="h-6 w-6 text-vs-royal" /></div>
              <h1 className="font-display font-bold text-2xl text-vs-ink tracking-tight">Verify your email</h1>
              <p className="text-sm text-slate-500 mt-2">We've sent a verification code to<br /><span className="font-semibold text-slate-700">{maskEmail(form.email)}</span></p>
              <div className="mt-6"><OtpInput value={otp} onChange={setOtp} disabled={loading} testid="register-otp-input" /></div>
              {error && <div className="mt-4"><ErrorBox testid="otp-error">{error}</ErrorBox></div>}
              <Button onClick={doVerify} disabled={loading || otp.length !== 6} className="w-full h-11 mt-6 bg-vs-royal hover:bg-vs-blue text-white font-semibold" data-testid="verify-email-btn">
                <LoadingLabel loading={loading} text="Verifying…">Verify Email</LoadingLabel>
              </Button>
              <div className="mt-4 flex items-center justify-center gap-5 text-sm">
                <button onClick={doResend} disabled={cooldown > 0} className="text-vs-royal font-medium disabled:text-slate-400 transition-colors" data-testid="resend-code-btn">
                  {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend Code"}
                </button>
                <button onClick={() => { setStep(1); setOtp(""); setError(""); }} className="text-slate-500 hover:text-slate-700" data-testid="change-email-btn">Change Email</button>
              </div>
            </div>
          )}

          <p className="text-sm text-slate-500 mt-7 text-center">Already registered? <Link to="/login" className="text-vs-royal font-semibold hover:underline">Sign in</Link></p>
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------ Forgot Password ------------------------------ */
export function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch { setSent(true); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2"><BrandPanel />
      <div className="flex items-center justify-center px-5 py-8 sm:p-12 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <MobileBrand />
          <h1 className="font-display font-bold text-2xl sm:text-[28px] text-vs-ink tracking-tight">Reset your password</h1>
          {sent ? (
            <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-100 p-5" data-testid="forgot-success">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm"><MailCheck className="h-4 w-4" />Reset link sent</div>
              <p className="text-sm text-emerald-700/80 mt-1.5">If an account exists for <span className="font-medium">{email}</span>, a secure reset link has been sent. Please check your inbox.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <p className="text-sm text-slate-500">Enter your work email and we'll send a secure reset link.</p>
              <div><Label className="text-slate-700">Work Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" className="mt-1.5" placeholder="you@company.com" /></div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-vs-royal hover:bg-vs-blue text-white font-semibold" data-testid="forgot-submit-btn">
                <LoadingLabel loading={loading} text="Sending…">Send reset link<ArrowRight className="ml-1.5 h-4 w-4" /></LoadingLabel>
              </Button>
            </form>
          )}
          <p className="text-sm text-slate-500 mt-6 text-center"><Link to="/login" className="text-vs-royal font-semibold hover:underline">← Back to sign in</Link></p>
        </motion.div>
      </div>
    </div>
  );
}

/* ------------------------------ Reset Password ------------------------------ */
export function ResetPassword() {
  const loc = useLocation(); const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token");
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true); setError("");
    try { await api.post("/auth/reset-password", { token, password }); toast.success("Password updated"); nav("/login"); }
    catch (err) { setError(friendlyAuthError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid lg:grid-cols-2"><BrandPanel />
      <div className="flex items-center justify-center px-5 py-8 sm:p-12 bg-slate-50">
        <motion.form initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="w-full max-w-md space-y-4">
          <MobileBrand />
          <h1 className="font-display font-bold text-2xl sm:text-[28px] text-vs-ink tracking-tight">Set a new password</h1>
          <p className="text-sm text-slate-500">Choose a strong password for your account.</p>
          <div>
            <Label className="text-slate-700">New password</Label>
            <PasswordInput id="reset-password" value={password} onChange={(e) => setPassword(e.target.value)} testid="reset-password-input" placeholder="Create a strong password" autoComplete="new-password" />
            <PasswordRules password={password} />
          </div>
          {error && <ErrorBox testid="reset-error">{error}</ErrorBox>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-vs-royal hover:bg-vs-blue text-white font-semibold" data-testid="reset-submit-btn">
            <LoadingLabel loading={loading} text="Updating…">Update password</LoadingLabel>
          </Button>
        </motion.form>
      </div>
    </div>
  );
}
